/**
 * Stocks API — positions / compare(당일손익) / transactions / pnl
 */
import { apiClient } from "./client";
import {
  DividendResponse,
  PortfolioPosition,
  PortfolioTransaction,
  RealizedPnlResponse,
  StockPriceCompareResult,
} from "src/models/Portfolio";

/** 미실현 손익 (당일/전체) */
export interface UnrealizedPnlResponse {
  stock_id: string;
  stock_name: string;
  brokerage: string;
  account_type: string;
  trade_currency: string;
  current_quantity: number;
  avg_price: number;
  current_price: number;
  current_value_krw: number;
  unrealized_gain_krw: number;
  unrealized_gain_percent: number;
}

export const stocksApi = {
  /** 보유 포지션 */
  getPositions: (userIds?: string[]) =>
    apiClient.get<PortfolioPosition[]>("/v1/portfolio/stocks/positions", {
      user_ids: userIds?.join(","),
    }),

  /** 당일 주가 비교 — 당일손익에는 반드시 _krw 필드 사용 */
  comparePrices: (days: number, userIds?: string[]) =>
    apiClient.get<StockPriceCompareResult[]>("/v1/portfolio/stocks/prices/compare", {
      days,
      user_ids: userIds?.join(","),
    }),

  /** 거래 내역 */
  getTransactions: (userIds?: string[]) =>
    apiClient.get<PortfolioTransaction[]>("/v1/portfolio/stocks/transactions", {
      user_ids: userIds?.join(","),
    }),

  /** 배당 내역 (기간 지정) */
  getDividends: (startDate: string, endDate: string, userIds?: string[]) =>
    apiClient.get<DividendResponse[]>("/v1/portfolio/stocks/dividends", {
      start_date: startDate,
      end_date: endDate,
      user_ids: userIds?.join(","),
    }),

  /** 신규 거래 추가 */
  createTransaction: (tx: {
    stock_id: string;
    transaction_type: string;
    quantity: number;
    price: number;
    brokerage: string;
    account_type: string;
    transaction_date: string;
  }) => apiClient.post("/v1/portfolio/stocks/transactions", tx),

  /** 거래 수정 */
  updateTransaction: (
    transactionId: number,
    tx: {
      stock_id: string;
      transaction_type: string;
      quantity: number;
      price: number;
      brokerage: string;
      account_type: string;
      transaction_date: string;
    },
  ) => apiClient.put(`/v1/portfolio/stocks/transactions/${transactionId}`, tx),

  /** 거래 삭제 */
  deleteTransaction: (transactionId: number) =>
    apiClient.delete(`/v1/portfolio/stocks/transactions/${transactionId}`),

  /** 실현 손익 */
  getRealizedPnl: (filter: { user_ids?: string[]; start_date: string; end_date: string }) =>
    apiClient.get<RealizedPnlResponse[]>("/v1/portfolio/stocks/pnl/realized_pnl", {
      ...filter,
      user_ids: filter.user_ids?.join(","),
    }),

  /** 미실현 손익 */
  getUnrealizedPnl: (filter: { user_ids?: string[]; start_date: string; end_date: string }) =>
    apiClient.get<UnrealizedPnlResponse[]>("/v1/portfolio/stocks/pnl/unrealized_pnl", {
      ...filter,
      user_ids: filter.user_ids?.join(","),
    }),

  /** 총손익 요약 (이동평균법) — 실현 + 미실현 + 배당 = 총손익 */
  getTotalPnlSummary: (filter: { user_ids?: string[]; start_date?: string; end_date?: string }) =>
    apiClient.get<TotalPnlSummary>("/v1/portfolio/stocks/pnl/total-summary", {
      ...filter,
      user_ids: filter.user_ids?.join(","),
    }),

  /** 회복률 (52주 고점 대비) — stock_id 미지정 시 포트폴리오 전체 */
  getRecoveryRate: (stockId?: string) =>
    apiClient.get<any>("/v1/portfolio/recovery-rate", {
      stock_id: stockId,
    }),
};

export interface TotalPnlRow {
  stock_id: string;
  stock_name: string;
  trade_currency: string;
  realized_quantity: number;
  realized_pnl: number;
  realized_pnl_krw: number;
  unrealized_pnl: number;
  unrealized_pnl_krw: number;
  holding_qty: number;
  avg_price: number | null;
  current_price: number | null;
  dividend_krw: number;
  invested_krw: number;
  pnl_rate: number | null;
  total_pnl_krw: number;
}

export interface TotalPnlSummary {
  user_ids: string[];
  start_date: string | null;
  end_date: string | null;
  rows: TotalPnlRow[];
  total_realized_pnl_krw: number;
  total_unrealized_pnl_krw: number;
  total_dividend_krw: number;
  total_invested_krw: number;
  total_pnl_krw: number;
}
